import { useToast } from '@hover/blueprint';
import { useCallback } from 'react';
import { generatePath, useHistory } from 'react-router';

import {
  getReferredProperty,
  imageTracking,
} from 'src/modules/homeownerApp/components/Customize/tracking';
import type {
  AiGeneratedImageFragment,
  Project,
} from 'src/shared/graphql/generated';
import { useGetProjectsQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { Route } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

import {
  imageIsAddedToAnyProject,
  useCreateAiDisplayImageAndUpdateCache,
  useDeleteAiDisplayImageAndUpdateCache,
} from '../components/Utils';
import type { ProjectFragment } from '../graphql/generated';
import { HomeownerAppMenuNames } from '../providers/HomeownerAppProvider';
import { useSaveToProjectContext } from '../providers/SaveToProjectProvider';
import type { AiOrSampleDisplayImage } from '../types';
import { isAiGenerated, isGenerated, isSample } from '../types';

import { useActiveOriginalImage } from './useActiveOriginalImage';
import { useErrors } from './useErrors';
import { useHomeownerAppContext } from './useHomeownerAppContext';

interface BaseOptions {
  image?: AiOrSampleDisplayImage | AiGeneratedImageFragment | undefined | null;
}

export interface UseIsSavedToProjectOptions extends BaseOptions {}

const getSiblingDisplayImageId = (
  { addables }: ProjectFragment,
  existingAiDisplayImageId: number,
) =>
  addables?.find(
    ({ aiGeneratedImage: { id } }) => id === existingAiDisplayImageId,
  )?.id;

export const useIsSavedToProject = ({ image }: UseIsSavedToProjectOptions) => {
  const { data } = useGetProjectsQuery();
  const imageId = isGenerated(image) ? image.id : image?.aiGeneratedImage.id;

  return (
    !isSample(image) && imageIsAddedToAnyProject(data?.projects || [], imageId)
  );
};

export interface UseSaveToProjectOptions extends BaseOptions {
  location: string;
  referreringProject?: Project;
}

export const useSaveToProject = ({
  image,
  referreringProject,
  location,
  ...rest
}: UseSaveToProjectOptions) => {
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();
  const { image: originalImage } = useActiveOriginalImage({
    originalImageId: isSample(image)
      ? image?.sampleId
      : image?.aiOriginalImageId,
  });
  const { setImageToSave } = useSaveToProjectContext() ?? {};
  const isSavedToProject = useIsSavedToProject({ image, ...rest });
  const createAiDisplayImage = useCreateAiDisplayImageAndUpdateCache();
  const deleteAiDisplayImage = useDeleteAiDisplayImageAndUpdateCache();
  const toast = useToast();
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { showError } = useErrors();

  const formatError = useCallback(
    (errorMessage: string): string =>
      t('customize.addToProject.addToProjectFailure', { errorMessage }),
    [t],
  );

  const handleAddToProject = async (
    project?: ProjectFragment | null,
    generatedImageToSave?: AiGeneratedImageFragment,
  ) => {
    if (!project) return;

    const { id: projectId, name: projectName } = project;

    if (generatedImageToSave && projectId && projectName) {
      try {
        buttonPressed({
          ...trackingData,
          ...imageTracking(generatedImageToSave),
          ...getReferredProperty('Project', true),
          button_location: 'Save to module',
          button_text: projectName,
          partner_id: partnerId,
          primary_cta: false,
          project_id: projectId,
          track_id: 'save-to-project',
        });

        await createAiDisplayImage(generatedImageToSave, projectId);

        buttonPressed({
          ...trackingData,
          ...imageTracking(generatedImageToSave),
          ...getReferredProperty('Project', true),
          button_location: 'Save to module',
          button_text: projectName,
          partner_id: partnerId,
          primary_cta: false,
          project_id: projectId,
          track_id: 'image-saved',
        });

        const navigateToProjectAndCloseToast = () => {
          toast.closeAll();
          history.push(
            generatePath(Route.HOMEOWNER_APP_PROJECT.path, { projectId }),
          );
        };

        toast({
          position: 'top',
          props: {
            message: t('images.addedToProject', {
              projectName,
            }),
            action: {
              callback: navigateToProjectAndCloseToast,
              label: t('images.viewProject'),
            },
          },
        });
      } catch (error) {
        logError(error as Error);
        showError({ error: error as Error, formatError });
      }
    }
  };

  const handleRemoveFromProject = async (
    project?: ProjectFragment | null,
    generatedImageToSave?: AiGeneratedImageFragment,
  ) => {
    if (!project) return;

    const { name: projectName } = project;
    if (generatedImageToSave?.id && project) {
      try {
        const imageId = getSiblingDisplayImageId(
          project,
          generatedImageToSave.id,
        );
        if (imageId) {
          buttonPressed({
            ...trackingData,
            ...imageTracking(generatedImageToSave),
            ...getReferredProperty('Project', false),
            button_location: 'Save to module',
            button_text: projectName,
            partner_id: partnerId,
            primary_cta: false,
            track_id: 'remove-from-project',
          });

          await deleteAiDisplayImage(imageId, project.id);

          buttonPressed({
            ...trackingData,
            ...imageTracking(generatedImageToSave),
            ...getReferredProperty('Project', false),
            button_location: 'Save to module',
            button_text: projectName,
            partner_id: partnerId,
            primary_cta: false,
            track_id: 'image-removed',
          });
        }
        toast({
          description: t('images.removedFromProject', {
            projectName,
          }),
          position: 'top',
        });
      } catch (error) {
        logError(error as Error);
        showError({ error: error as Error, formatError });
      }
    }
  };

  const handleSave = () => {
    if (!image) return;

    if (isSample(image)) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
      return;
    }

    const unassociatedProject = originalImage?.projects?.[0];
    const preSetProject =
      originalImage?.associatedProject ??
      referreringProject ??
      unassociatedProject;

    if (!preSetProject) {
      setImageToSave(image);
      return;
    }

    const generatedImageToSave = !isAiGenerated(image)
      ? image.aiGeneratedImage
      : image;

    if (isSavedToProject) {
      void handleRemoveFromProject(preSetProject, generatedImageToSave);
    } else {
      void handleAddToProject(preSetProject, generatedImageToSave);
    }

    buttonPressed({
      ...trackingData,
      ...imageTracking(image),
      button_location: location,
      button_text: 'Save',
      partner_id: partnerId,
      primary_cta: false,
      track_id: 'save-image',
    });
  };

  return {
    isSavedToProject,
    handleSave,
    handleAddToProject,
    handleRemoveFromProject,
  };
};
